import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import { useApi } from '../../../hooks/useApi'
import Breadcrumbs from '../../structure/Breadcrumbs'

interface LogEvent {
  ingestionTime: number
  timestamp: number
  message: string
}

const LogsStreams: React.FC = () => {
  const { callApi } = useApi()
  const params = useParams()
  const id = params.id
  const name = window.location.href.split('/groups?name=')[1]
  const [events, setLogsEvents] = useState<LogEvent[] | null>(null)
  const [next, setNext] = useState<string | null>(null)
  const [prev, setPrev] = useState<string | null>(null)
  const [nextButton, setNextButton] = useState({ disabled: false, message: 'Next events' })
  const [prevButton, setPrevButton] = useState({ disabled: false, message: 'Previous events' })

  const fetchData = async () => {
    try {
      const response = await callApi(`/admin/logs/${name}/${id}`)
      setLogsEvents(response.events)
      setNext(response.nextToken || null)
      setPrev(response.nextBackwardToken || null)
    } catch (error: any) {
      console.error('Token list error:', error)
    }
  }

  useEffect(() => { fetchData() }, [])

  const handleSeeMore = async () => {
    try {
      const response = await callApi(`/admin/logs/${name}/${id}`, { nextToken: next })
      if (response.events && response.events.length > 0) {
        setLogsEvents(events ? response.events.concat(events) : null)
        setNext(response.nextToken || null)
      } else if (response?.events?.length === 0) {
        setNextButton({ disabled: true, message: 'No more next events' })
      }
    } catch (error) {
      console.error('Error loading next events:', error)
    }
  }

  const handleSeePrev = async () => {
    try {
      const response = await callApi(`/admin/logs/${name}/${id}`, { nextToken: prev })
      if (response.nextBackwardToken) setPrev(response.nextBackwardToken)
      if (response.events && response.events.length > 0) {
        setLogsEvents(events ? response.events.concat(events) : null)
      } else if (response?.events?.length === 0) {
        setPrevButton({ disabled: true, message: 'No more previous events' })
      }
    } catch (error) {
      console.error('Error loading previous events:', error)
    }
  }

  const paths = [
    { name: 'Logs', url: '/dashboard/settings/logs' },
    { name: name ?? '', url: '/dashboard/settings/logs/groups?name=' + name },
    { name: id ?? '', url: '' },
  ]

  if (!events) return <LoaderBar />

  return (
    <Wrap>
      <Breadcrumbs paths={paths} />
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Timestamp</Th>
              <Th>Message</Th>
            </tr>
          </thead>
          <tbody>
            {events
              .sort((a, b) => b.timestamp - a.timestamp)
              .map((item, index) => (
                <tr key={index}>
                  <TimeTd><Mono>{new Date(item.timestamp).toLocaleString()}</Mono></TimeTd>
                  <Td><MessageCell message={item.message} /></Td>
                </tr>
              ))}
          </tbody>
        </Table>
      </TableWrap>
      {(prev || next) && (
        <PaginationRow>
          {prev && (
            <PageBtn onClick={handleSeePrev} disabled={prevButton.disabled}>
              {prevButton.message}
            </PageBtn>
          )}
          {next && (
            <PageBtn onClick={handleSeeMore} disabled={nextButton.disabled}>
              {nextButton.message}
            </PageBtn>
          )}
        </PaginationRow>
      )}
    </Wrap>
  )
}

const MessageCell: React.FC<{ message: string }> = ({ message }) => {
  const [expanded, setExpanded] = useState(false)
  const limit = 120

  return (
    <>
      <span>{expanded ? message : message.substring(0, limit)}</span>
      {message.length > limit && (
        <ShowMoreBtn onClick={() => setExpanded(!expanded)}>
          {expanded ? 'Hide' : 'Show more'}
        </ShowMoreBtn>
      )}
    </>
  )
}

export default LogsStreams

// ─── Styles ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`display: flex; flex-direction: column; gap: 12px;`

const shimmer = keyframes`0% { transform: translateX(-100%); } 100% { transform: translateX(100%); }`
const LoaderBar = styled.div`
  height: 2px; background: rgba(255,255,255,0.06); border-radius: 2px; overflow: hidden; position: relative;
  &::after { content: ''; display: block; position: absolute; inset: 0; background: linear-gradient(90deg, transparent, rgba(111,92,230,0.5), transparent); animation: ${shimmer} 1.4s ease infinite; }
`

const TableWrap = styled.div`overflow-x: auto; border: 1px solid rgba(255,255,255,0.07); border-radius: 8px;`
const Table = styled.table`width: 100%; border-collapse: collapse; font-size: 13px;`
const Th = styled.th`padding: 10px 12px; text-align: left; color: #a1a1a1; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; border-bottom: 1px solid rgba(255,255,255,0.06);`
const Td = styled.td`padding: 8px 10px; color: #a0a0a0; font-size: 13px; vertical-align: top; border-bottom: 1px solid rgba(255,255,255,0.04); max-width: 600px; overflow-wrap: break-word; word-wrap: break-word; tr:last-child & { border-bottom: none; }`
const TimeTd = styled(Td)`white-space: nowrap; vertical-align: middle;`
const Mono = styled.span`font-family: 'SF Mono','Fira Code',monospace; font-size: 11px; color: #777;`

const PaginationRow = styled.div`display: flex; gap: 8px;`
const PageBtn = styled.button`
  height: 28px; padding: 0 12px; background: rgba(111,92,230,0.1); border: 1px solid rgba(111,92,230,0.3);
  border-radius: 5px; color: #a89aff; font-size: 12px; font-weight: 500; cursor: pointer;
  transition: background 0.15s; &:hover:not(:disabled) { background: rgba(111,92,230,0.2); border-color: rgba(111,92,230,0.5); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`

const ShowMoreBtn = styled.button`
  margin-left: 6px; background: transparent; border: none; color: #6f5ce6; font-size: 11px; cursor: pointer; padding: 0;
  &:hover { color: #a89aff; }
`
